import { computed, type MaybeRefOrGetter, toValue } from "vue";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import { AccessFeature } from "@/shared/access-control/access-control.ts";
import { type ElementMapper, type GuiElementTypeMap, GuiElementTypes, type ViewContext } from "./context.types.ts";
import { useGuiElements } from "./gui-elements.ts";
import { mapButtonElement, mapCardElement } from "./context.mapper.ts";

/** Type-safe mapper registry — maps each GuiElementTypes to its corresponding mapper */
const mappers: { [K in GuiElementTypes]: ElementMapper<GuiElementTypeMap[K]> } = {
    [GuiElementTypes.buttons]: mapButtonElement,
    [GuiElementTypes.cards]: mapCardElement,
};

/**
 * Main composable for fetching and resolving context GUI elements.
 * The return type is automatically inferred from the `type` parameter
 * via {@link GuiElementTypeMap}.
 */
export const useContextElements = <TType extends GuiElementTypes>(
    type: TType,
    context: MaybeRefOrGetter<ViewContext>
) => {
    const { hasAccess } = useHasAccess({ featureID: AccessFeature.extendable_gui });

    const contextData = computed<ViewContext>(() => {
        const props = toValue(context);
        return {
            contentMapId: props.contentMapId,
            mechanicPath: props.mechanicPath,
            mechanicId: props.mechanicId,
            assemblyId: props.assemblyId,
            productId: props.productId,
            orderable: props.orderable,
            articleId: props.articleId,
            topicId: props.topicId,
            assetId: props.assetId,
            view: props.view,
        };
    });

    const mapper = mappers[type] as ElementMapper<GuiElementTypeMap[TType]>;

    const { elements, loading, error } = useGuiElements<GuiElementTypeMap[TType]>(type, {
        context: contextData,
        hasAccess,
        mapper,
    });

    const openUrl = (url: string, openInCurrentTab: boolean) => {
        if (openInCurrentTab) {
            window.location.href = url;
        } else {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    return { elements, loading, error, openUrl };
};
